<template>
  <div class="task-list">
    <div class="toolbar">
      <h3>切片任务监控</h3>
      <div class="toolbar-actions">
        <el-button
          size="small"
          type="danger"
          :disabled="!selectedTasks.length"
          @click="confirmDelete"
        >
          删除{{ selectedTasks.length ? ` (${selectedTasks.length})` : '' }}
        </el-button>
        <el-button size="small" @click="loadTasks">刷新</el-button>
      </div>
    </div>

    <el-form :inline="true" size="small" class="filter-form">
      <el-form-item label="用户邮箱">
        <el-input v-model="filters.email" clearable placeholder="模糊搜索" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="任务 ID">
        <el-input v-model="filters.taskId" clearable placeholder="模糊搜索" @keyup.enter.native="onSearch" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filters.status" clearable placeholder="全部">
          <el-option label="等待中" value="PENDING" />
          <el-option label="进行中" value="RUNNING" />
          <el-option label="成功" value="SUCCESS" />
          <el-option label="失败" value="FAILED" />
          <el-option label="已取消" value="CANCELLED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">查询</el-button>
        <el-button @click="onReset">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table
      ref="taskTable"
      v-loading="loading"
      :data="tasks"
      stripe
      :default-sort="{ prop: sortProp, order: sortOrder }"
      @selection-change="handleSelectionChange"
      @sort-change="onSortChange"
    >
      <el-table-column type="selection" width="48" />
      <el-table-column prop="userEmail" label="用户账号" min-width="170" sortable="custom" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.userEmail || (row.userId ? row.userId : '—') }}</template>
      </el-table-column>
      <el-table-column prop="id" label="任务 ID" min-width="180" sortable="custom" />
      <el-table-column prop="projectId" label="项目 ID" min-width="160" />
      <el-table-column label="创建时间" min-width="170" sortable="custom" prop="createdAt">
        <template slot-scope="{ row }">
          {{ formatTime(row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="切片参数" width="100">
        <template slot-scope="{ row }">
          {{ formatSegmentParam(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="segmentMode" label="模式" width="110">
        <template slot-scope="{ row }">
          {{ segmentModeLabel(row.segmentMode) }}
        </template>
      </el-table-column>
      <el-table-column label="存储" width="100">
        <template slot-scope="{ row }">
          <el-tag v-if="row.storagePurgedAt" size="mini" type="info">已清理</el-tag>
          <el-tag v-else size="mini" type="success">正常</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100" sortable="custom" />
      <el-table-column prop="progressPercent" label="进度" width="90" sortable="custom" />
      <el-table-column prop="progressMessage" label="说明" min-width="140" show-overflow-tooltip />
      <el-table-column prop="errorMessage" label="错误" min-width="160" show-overflow-tooltip />
      <el-table-column label="操作" width="120" fixed="right">
        <template slot-scope="{ row }">
          <el-button
            size="mini"
            type="warning"
            :disabled="row.status === 'RUNNING'"
            @click="retry(row)"
          >
            重跑
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :current-page="page + 1"
        :page-size="size"
        :total="total"
        @current-change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import { deleteSegmentTasks, fetchSegmentTasks, retrySegmentTask } from '../api/admin';

export default {
  name: 'TaskList',
  data() {
    return {
      loading: false,
      tasks: [],
      selectedTasks: [],
      page: 0,
      size: 10,
      total: 0,
      filters: { email: '', taskId: '', status: '' },
      sortBy: 'createdAt',
      sortDir: 'desc',
    };
  },
  computed: {
    sortProp() {
      const map = {
        createdAt: 'createdAt',
        userEmail: 'userEmail',
        id: 'id',
        status: 'status',
        progressPercent: 'progressPercent',
      };
      return map[this.sortBy] || 'createdAt';
    },
    sortOrder() {
      return this.sortDir === 'asc' ? 'ascending' : 'descending';
    },
  },
  mounted() {
    this.loadTasks();
  },
  methods: {
    async loadTasks() {
      this.loading = true;
      try {
        const data = await fetchSegmentTasks({
          page: this.page,
          size: this.size,
          email: this.filters.email || undefined,
          taskId: this.filters.taskId || undefined,
          status: this.filters.status || undefined,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        });
        this.tasks = data.items || [];
        this.total = data.total || 0;
        this.selectedTasks = [];
        this.$nextTick(() => {
          if (this.$refs.taskTable) {
            this.$refs.taskTable.clearSelection();
          }
        });
      } catch (e) {
        this.$message.error(e.message || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 0;
      this.loadTasks();
    },
    onReset() {
      this.filters = { email: '', taskId: '', status: '' };
      this.sortBy = 'createdAt';
      this.sortDir = 'desc';
      this.page = 0;
      this.loadTasks();
    },
    onSortChange({ prop, order }) {
      if (!prop || !order) return;
      const fieldMap = {
        createdAt: 'createdAt',
        userEmail: 'userEmail',
        id: 'id',
        status: 'status',
        progressPercent: 'progressPercent',
      };
      this.sortBy = fieldMap[prop] || 'createdAt';
      this.sortDir = order === 'ascending' ? 'asc' : 'desc';
      this.loadTasks();
    },
    handleSelectionChange(rows) {
      this.selectedTasks = rows || [];
    },
    onPageChange(currentPage) {
      this.page = currentPage - 1;
      this.loadTasks();
    },
    confirmDelete() {
      if (!this.selectedTasks.length) {
        return;
      }
      const running = this.selectedTasks.filter(item => item.status === 'RUNNING');
      if (running.length) {
        this.$message.warning('进行中的任务不可删除');
        return;
      }
      this.$confirm(
        `确定删除选中的 ${this.selectedTasks.length} 条任务记录吗？关联切片文件将一并删除。`,
        '删除确认',
        { type: 'warning' },
      ).then(() => {
        this.deleteSelected();
      }).catch(() => {});
    },
    async deleteSelected() {
      const taskIds = this.selectedTasks.map(item => item.id);
      try {
        const data = await deleteSegmentTasks(taskIds);
        this.$message.success(`已删除 ${data.deleted || taskIds.length} 条任务`);
        if (this.tasks.length === taskIds.length && this.page > 0) {
          this.page -= 1;
        }
        this.loadTasks();
      } catch (e) {
        this.$message.error(e.message || '删除失败');
      }
    },
    async retry(row) {
      try {
        await retrySegmentTask(row.id);
        this.$message.success('已提交重跑');
        this.loadTasks();
      } catch (e) {
        this.$message.error(e.message || '重跑失败');
      }
    },
    formatTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString('zh-CN', { hour12: false });
    },
    formatSegmentParam(row) {
      if (row.segmentMode === 'FIXED_COUNT') {
        return row.segmentCount ? `${row.segmentCount} 段` : '-';
      }
      if (row.durationSeconds) {
        return `${row.durationSeconds}s`;
      }
      return '-';
    },
    segmentModeLabel(mode) {
      const labels = {
        FIXED_DURATION: '固定时长',
        FIXED_COUNT: '按数量均分',
        HIGHLIGHT: '发现精彩',
      };
      return labels[mode] || mode || '-';
    },
  },
};
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar h3 {
  margin: 0;
}
.toolbar-actions {
  display: flex;
  gap: 8px;
}
.filter-form {
  margin-bottom: 12px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
